import { FORMAT_LABELS } from '@ugin/shared';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { CardPreview } from '@/domain/CardPreview';
import { useDeck, useDeckSimilar, useDeleteDeck } from '@/hooks/useDecks';
import { WidgetContainer } from '@/widgets/_base/WidgetContainer';
import { DeckVisualizerWidget } from '@/widgets/deck-visualizer';
import { MatchupHeatmapWidget } from '@/widgets/matchup-heatmap';

export function DeckDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useDeck(id);
  const similar = useDeckSimilar(id);
  const del = useDeleteDeck();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const deck = data?.deck;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (error || !deck) {
    return (
      <div className="rounded-lg border border-ugin-danger/40 bg-ugin-danger/10 p-6 text-sm text-ugin-danger">
        <p className="font-medium">This deck can't be found.</p>
        <p className="mt-1 text-ugin-text-muted">
          Maybe it's been deleted. <Link to="/decks" className="text-ugin-accent underline">Back to decks</Link>.
        </p>
      </div>
    );
  }

  const cardCount = deck.mainboard.reduce((s, e) => s + e.quantity, 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <Link to="/decks" className="inline-flex items-center gap-1 text-xs text-ugin-text-muted hover:text-ugin-text">
            <ArrowLeft className="size-3" />
            All decks
          </Link>
          <h1 className="font-display text-4xl tracking-wide text-ugin-text">{deck.name}</h1>
          <div className="flex items-center gap-2 text-sm text-ugin-text-muted">
            <Badge variant="accent" className="uppercase">{FORMAT_LABELS[deck.format]}</Badge>
            <span>·</span>
            <span>{cardCount} cards</span>
            {deck.sideboard?.length ? (
              <>
                <span>·</span>
                <span>{deck.sideboard.reduce((s, e) => s + e.quantity, 0)} sideboard</span>
              </>
            ) : null}
          </div>
        </div>
        <Button variant="outline" onClick={() => { setConfirmOpen(true); }} className="gap-1">
          <Trash2 className="size-3.5" />
          Delete
        </Button>
      </header>

      <DeckVisualizerWidget deckId={deck.id} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="xl:col-span-7">
          <MatchupHeatmapWidget format={deck.format} deckId={deck.id} />
        </div>
        <div className="xl:col-span-5">
          <WidgetContainer
            title="Closest in meta"
            loading={similar.isLoading}
            error={similar.error ?? undefined}
            empty={!similar.isLoading && (similar.data?.results.length ?? 0) === 0}
            emptyTitle="Nothing to compare against yet"
            emptyDescription={
              similar.data?.message ??
              'The nightly refresh builds the archetype decklists this compares against.'
            }
          >
            {similar.data?.source === 'snapshot' ? (
              <p className="mb-3 text-xs text-ugin-warn">
                Only archetype names are known for this format so far, so the overlap below is not
                yet meaningful. The nightly refresh fetches the decklists.
              </p>
            ) : null}
            <ul className="space-y-2">
              {(similar.data?.results ?? []).slice(0, 6).map((r) => (
                <li
                  key={r.archetype}
                  className="rounded-lg border border-ugin-border/40 bg-ugin-bg-deep/30 p-3 text-sm"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-ugin-text">{r.archetype}</span>
                    <span className="font-mono text-xs text-ugin-accent">
                      {r.overlapPercent.toFixed(0)}% overlap
                    </span>
                  </div>
                  {r.missingCards.length > 0 ? (
                    <p className="mt-1.5 text-xs text-ugin-text-muted">
                      Missing:{' '}
                      {r.missingCards.slice(0, 4).map((c, i) => (
                        <span key={c.cardName}>
                          {i > 0 ? ', ' : ''}
                          <CardPreview name={c.cardName} />
                          <span> ×{c.quantity}</span>
                        </span>
                      ))}
                      {r.missingCards.length > 4 ? ` +${r.missingCards.length - 4}` : null}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </WidgetContainer>
        </div>
      </div>

      <WidgetContainer title="Mainboard">
        <ul className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {deck.mainboard.map((entry) => (
            <li key={entry.cardName} className="flex items-baseline justify-between gap-3 text-ugin-text">
              <span className="font-mono text-xs text-ugin-text-faint">{entry.quantity}×</span>
              <span className="flex-1 truncate">
                <CardPreview name={entry.cardName} />
              </span>
            </li>
          ))}
        </ul>
      </WidgetContainer>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Retire this deck?</DialogTitle>
            <DialogDescription>
              "{deck.name}" will be removed from your library along with its analysis history.
              Match records for this deck stay, but unlink from it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setConfirmOpen(false); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!deck.id) return;
                try {
                  await del.mutateAsync(deck.id);
                  toast.success(`Retired "${deck.name}".`);
                  void navigate('/decks');
                } catch {
                  toast.error('Could not retire the deck.');
                }
              }}
              disabled={del.isPending}
            >
              {del.isPending ? 'Retiring…' : 'Retire'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
